'use client';
import React, { useRef, useState } from 'react';
import { Button, Icon } from '@benzinga/core-ui';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { generateCalendar } from './calendar';
import Hooks from '@benzinga/hooks';

interface AddToCalendarButtonProps {
  date: Date;
  description?: string;
  location: string;
  title: string;
}

export const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({ date, description, location, title }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const ref = useRef(null);

  Hooks.useClickOutside(ref, () => setIsDropdownOpen(false));

  const handleToggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const calendarProviders = [
    { icon: GoogleCalendarIcon, name: 'Google' },
    { icon: OutlookCalendarIcon, name: 'Outlook' },
    { icon: Apple, name: 'Apple' },
    { icon: Yahoo, name: 'Yahoo' },
  ];

  const handleAddToCalendar = (name: string) => {
    const href = generateCalendar({
      description,
      duration: 60,
      location,
      start: date,
      title,
    });
    return href[name.toLowerCase()];
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        className="uppercase h-12 flex justify-between bg-blue-500 font-bold text-white border-0 w-56 hover:bg-blue-500 hover:bg-opacity-90"
        onClick={handleToggleDropdown}
      >
        Add To Calendar
        <div className="bg-opacity-5 bg-white border border-opacity-30 border-white flex h-6 items-center justify-center rounded-full w-6">
          <Icon className="text-white" icon={faPlus} style={{ fontSize: '0.6rem' }} />
        </div>
      </Button>
      {isDropdownOpen && (
        <div className="ring-1 ring-black ring-opacity-5 absolute bg-white flex flex-col overflow-hidden rounded shadow-lg top-14 w-56 z-30">
          <ul className="h-full w-full">
            {calendarProviders.map(provider => {
              const ProviderIcon = provider.icon;
              return (
                <a
                  href={handleAddToCalendar(provider.name)}
                  key={provider.name}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  <li className="hover:bg-blue-100 cursor-pointer flex group items-center justify-between p-2">
                    <div className="flex">
                      <ProviderIcon />
                      <div className="ml-2">{provider.name} Calendar</div>
                    </div>
                    <Icon icon={faChevronRight} style={{ fontSize: '0.9em' }} />
                  </li>
                </a>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const GoogleCalendarIcon = () => (
  <span style={{ height: 25, width: 25 }}>
    <svg
      clipRule="evenodd"
      fillRule="evenodd"
      strokeLinejoin="round"
      strokeMiterlimit="2"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22 4.5v6H10v11H4v-15a2.015 2.015 0 012-2h16z"
        fill="#4285F4"
        fillRule="nonzero"
        transform="translate(-5.333 -5.333) scale(1.33333)"
      ></path>
      <path
        d="M22 27.5v-6h6l-6 6z"
        fill="#EA4435"
        fillRule="nonzero"
        transform="translate(-5.333 -5.333) scale(1.33333)"
      ></path>
      <path d="M22 9.5H28V21.5H22z" fill="#FFBA00" transform="translate(-5.333 -5.333) scale(1.33333)"></path>
      <path d="M13 18.5H19V30.5H13z" fill="#00AC47" transform="matrix(0 1.33333 -1.33333 0 48.667 6)"></path>
      <path
        d="M28 6.5v4h-6v-6h4a2.015 2.015 0 012 2z"
        fill="#0066DA"
        fillRule="nonzero"
        transform="translate(-5.333 -5.333) scale(1.33333)"
      ></path>
      <path
        d="M10 21.5v6H6a2.015 2.015 0 01-2-2v-4h6z"
        fill="#188038"
        fillRule="nonzero"
        transform="translate(-5.333 -5.333) scale(1.33333)"
      ></path>
      <path
        d="M15.69 17.09c0 .89-.66 1.79-2.15 1.79a3.008 3.008 0 01-1.52-.39l-.08-.06.29-.82.13.08c.353.214.757.331 1.17.34.323.021.641-.091.88-.31a.862.862 0 00.25-.65c-.01-.73-.68-.99-1.31-.99h-.54v-.81h.54c.45 0 1.12-.22 1.12-.82 0-.45-.31-.71-.85-.71a1.88 1.88 0 00-1.04.34l-.14.1-.28-.79.07-.06c.456-.293.987-.45 1.53-.45 1.19 0 1.72.73 1.72 1.45a1.439 1.439 0 01-.81 1.3 1.522 1.522 0 011.02 1.46zM18.71 12.98v5.81h-.98V14l-.94.51-.21-.82 1.37-.71h.76z"
        fill="#4285F4"
        fillRule="nonzero"
        transform="translate(-5.333 -5.333) scale(1.33333)"
      ></path>
    </svg>
  </span>
);

const OutlookCalendarIcon = () => (
  <span style={{ height: 25, width: 25 }}>
    <svg
      clipRule="evenodd"
      fillRule="evenodd"
      strokeLinejoin="round"
      strokeMiterlimit="2"
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fillRule="nonzero">
        <path
          d="M479.689 121.431H270.912c-8.23 0-14.91 6.705-14.91 14.958 0 8.251 6.68 14.95 14.91 14.95h165.322l-91.621 71.447-65.709-41.901-15.984 25.245 74.559 47.548c2.418 1.56 5.221 2.332 7.996 2.332 3.221 0 6.473-1.045 9.16-3.14l110.143-85.924v193.736H270.912c-8.23 0-14.91 6.699-14.91 14.946 0 8.257 6.68 14.959 14.91 14.959h208.777c8.234 0 14.914-6.702 14.914-14.959v-239.24c.001-8.252-6.679-14.957-14.914-14.957z"
          fill="#1B80E4"
          transform="translate(-18.665 -18.666) scale(1.07291)"
        ></path>
        <path
          d="M280.428 35.153c-3.404-2.841-7.967-4.069-12.26-3.168L29.566 76.84c-7.068 1.322-12.169 7.481-12.169 14.687v328.966c0 7.177 5.101 13.364 12.169 14.688l238.602 44.856c.893.177 1.818.271 2.744.271 3.459 0 6.828-1.199 9.516-3.445 3.432-2.836 5.393-7.083 5.393-11.512V46.671c-.001-4.458-1.962-8.676-5.393-11.518z"
          fill="#2299F8"
          transform="translate(-18.665 -18.666) scale(1.07291)"
        ></path>
        <path
          d="M151.609 360.683c-41.1 0-74.563-40.253-74.563-89.72 0-49.462 33.464-89.719 74.563-89.719 41.1 0 74.565 40.257 74.565 89.719 0 49.467-33.465 89.72-74.565 89.72zm0-149.53c-24.665 0-44.736 26.824-44.736 59.81 0 32.988 20.071 59.813 44.736 59.813 24.666 0 44.743-26.824 44.743-59.813 0-32.985-20.077-59.81-44.743-59.81z"
          fill="#FCFCFC"
          transform="translate(-18.665 -18.666) scale(1.07291)"
        ></path>
      </g>
    </svg>
  </span>
);

const Apple = () => (
  <span style={{ height: 25, width: 25 }}>
    <svg version="1.1" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" xmlSpace="preserve">
      <path
        clipRule="evenodd"
        d="M368.003 117.456c-44.426 0-68.012 22.217-102.915 21.764-34.912.453-58.49-21.764-102.922-21.764-35.364 0-80.257 21.764-100.657 53.511-37.629 58.048-34.005 121.088-18.135 186.378C60.603 426.744 121.358 512 169.421 512c39.441 0 57.131-21.325 95.667-21.325 38.526 0 55.756 21.325 94.76 21.325 44.425 0 97.472-73.023 120.152-137.876-43.534-17.232-73.01-60.306-73.01-106.113 0-45.346 23.133-80.264 58.498-100.215-21.759-29.482-63.48-50.34-97.485-50.34zM356.209 0c-63.918 6.804-111.521 60.313-107.444 121.081C312.691 114.731 359.848 60.767 356.209 0z"
        fill="#C6C7C9"
        fillRule="evenodd"
      ></path>
    </svg>
  </span>
);
const Yahoo = () => (
  <span style={{ height: 25, width: 25 }}>
    <svg
      clipRule="evenodd"
      fillRule="evenodd"
      strokeLinejoin="round"
      strokeMiterlimit="2"
      viewBox="0 0 76 76"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <g>
          <path
            d="M75 7.238A7.241 7.241 0 0067.762 0H7.238A7.241 7.241 0 000 7.238v60.524A7.241 7.241 0 007.238 75h60.524A7.241 7.241 0 0075 67.762V7.238z"
            fill="#673276"
          ></path>
          <g fill="#FEFEFE">
            <path d="M60.983 25l-.207-.222-3.138.03c-4.84.088-9.754.133-14.609.222l-3.02.014-.222.223v2.886l.222.207 1.835.089 5.551.222.266.015-.31.296c-3.242 2.723-6.424 5.447-9.666 8.2l-.474-.045c-2.753-3.404-5.565-6.823-8.303-10.287l-1.939-2.398h.103l3.183-.281 3.389-.251.222-.222.326-2.902-.222-.281H9.118l-.222.281v2.798l.325.326 6.957.488.43.207 6.039 7.416c2.694 3.256 5.387 6.498 8.096 9.739l.104.37v8.571l-.163.222-3.508.296-4.204.325-.266.237.044 2.428.222.266h24.586l.103-.148v-2.59l-.103-.193-1.125-.029-6.587-.267-.266-.281c.103-3.064.163-6.113.207-9.207l.163-.251 3.241-2.872c3.464-3.079 6.957-6.187 10.406-9.28l.326-.163 1.509-.104 5.344-.414.207-.237V25zM66.104 32.549l-.162.562-1.51 6.469-1.776 7.223-.104.089-2.531-.281-.163-.015-.104-.074v-.903l.045-.829.059-2.487.045-2.723.118-5.388.044-1.909.06-.119 1.569.119 1.021.044 1.88.104 1.184.029.325.03v.059zM62.878 51.762h-3.286l-.119-.074-.163-1.54-.103-1.51.059-.029 1.998.148 1.347.103.267.03.103.074v2.724l-.103.074z"></path>
          </g>
        </g>
      </g>
    </svg>
  </span>
);
